// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/implementation/qa-criteria-system.md

import React from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  LinearProgress,
  Chip
} from '@mui/material';
import { Add, Warning } from '@mui/icons-material';
import CategoryEditor from '../CategoryEditor';

/**
 * CategoriesStep Component (Step 3 of 4)
 * 
 * Build categories and metrics structure:
 * - Add/Edit/Remove categories
 * - Add/Edit/Remove metrics per category
 * - Real-time weight validation
 */
const CategoriesStep = ({
  formData,
  errors,
  onCategoryChange,
  onAddCategory,
  onDeleteCategory
}) => {
  // Calculate total category weight
  const totalCategoryWeight = formData.categories.reduce(
    (sum, cat) => sum + (cat.weight_percentage || 0),
    0
  );

  const categoryWeightError = totalCategoryWeight !== 100;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Step 3: Categories & Metrics
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Define the categories and their metrics. Total category weights must equal 100%.
      </Typography>

      {/* Category Weight Progress */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2">
            Total Category Weight
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography 
              variant="body2" 
              color={categoryWeightError ? 'error' : 'success.main'}
              fontWeight="medium"
            >
              {totalCategoryWeight}% / 100%
            </Typography>
            {categoryWeightError && (
              <Chip 
                icon={<Warning />}
                label="Must be 100%"
                size="small"
                color="error"
              />
            )}
          </Box>
        </Box>
        <LinearProgress
          variant="determinate"
          value={Math.min(totalCategoryWeight, 100)}
          color={categoryWeightError ? 'error' : 'success'}
          sx={{ height: 8, borderRadius: 1 }}
        />
      </Box>

      {/* Validation Error */}
      {errors.categories && typeof errors.categories === 'string' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errors.categories}
        </Alert>
      )}

      {/* Categories List */}
      {formData.categories.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            No categories defined yet. Click "Add Category" to create your first category.
          </Typography>
        </Alert>
      ) : (
        <Box sx={{ mb: 3 }}>
          {formData.categories.map((category, index) => (
            <CategoryEditor
              key={index}
              category={category}
              index={index}
              onChange={onCategoryChange}
              onDelete={onDeleteCategory}
              error={errors.categoryErrors?.[index]}
              expanded={true}
              criteriaType={formData.criteria_type || 'matrix'}
            />
          ))}
        </Box>
      )}

      {/* Add Category Button */}
      <Button
        variant="outlined"
        startIcon={<Add />}
        onClick={onAddCategory}
        size="large"
        fullWidth
        sx={{ mb: 2 }}
      >
        Add Category
      </Button>

      {/* Help Alert */}
      <Alert severity="info">
        <Typography variant="body2">
          <strong>Tips:</strong>
          <br />
          • Category weights must total 100%
          <br />
          • Each category must have at least one metric
          <br />
          • Metric weights within each category must total that category's weight
          <br />
          • You can expand/collapse categories to manage their metrics
        </Typography>
      </Alert>
    </Box>
  );
};

export default CategoriesStep;
